import { Suspense, useMemo } from 'react';
import { RouteObject } from 'react-router-dom';
import { Outlet } from 'react-router-dom';

import AuthRoute from 'app/components/shared/AuthRoute';
import AuthRouteWithCompanyType from 'app/components/shared/AuthRouteWithCompanyType';
import { keys } from 'app/config/keys';
import { routes } from 'app/constants/routes';
import { CompanyTypeId } from 'app/constants/user';
import LoadingScreen from 'app/pages/shared/LoadingScreen';
import RedirectToHome from 'app/pages/shared/RedirectToHome';

import {
  Auth,
  BrokerOAuth,
  BrokerOAuthLandingPage,
  ColorDocs,
  DisplayAllBrokersPage,
  FourOhFour,
  GeneralOAuth,
  GetMobileApp,
  Icons,
  InactiveCompanyLandingPage,
  LinkLoadPayAccount,
  Login,
  MissingDetails,
  OktaCallback,
  OktaError,
  OktaExternal,
  OktaLogin,
  OktaSso,
  RegisterPayee,
  Sandbox,
  SetPassword,
  TriumphInternalLandingPage,
} from './MainRouteComponents';

export const useGetMainRoutes = (): RouteObject[] => {
  const isProduction = keys.NODE_ENV === 'production';

  return useMemo(() => [
    {
      children: [
        {
          element: <Login />,
          path: '',
        },
        {
          element: <Login />,
          path: routes.login,
        },
        {
          element: <OktaCallback />,
          path: routes.oktaCallback,
        },
        {
          element: <OktaExternal />,
          path: routes.oktaExternal,
        },
        {
          element: <OktaError />,
          path: routes.oktaError,
        },
        {
          element: <OktaLogin />,
          path: routes.oktaLogin,
        },
        {
          element: <OktaSso />,
          path: routes.oktaSso,
        },
        {
          element: <Auth />,
          path: routes.auth,
        },
        {
          element: <DisplayAllBrokersPage />,
          path: routes.displayAllBrokers,
        },
        {
          element: <RegisterPayee />,
          path: 'Login/RegisterAccount/:relationshipId',
        },
        {
          element: <RegisterPayee />,
          path: routes.registerPayee,
        },
        {
          element: <RedirectToHome />,
          path: `${routes.factorLandingPage}/:id`,
        },
        {
          element: (
            <AuthRoute>
              <MissingDetails />
            </AuthRoute>
          ),
          path: `${routes.missingDetails}/:missingDetail`,
        },
        {
          element: (
            <AuthRoute>
              <GetMobileApp />
            </AuthRoute>
          ),
          path: routes.getMobileApp,
        },
        {
          element: (
            <AuthRoute>
              <InactiveCompanyLandingPage />
            </AuthRoute>
          ),
          path: routes.inactive,
        },
        {
          element: (
            <AuthRouteWithCompanyType permittedCompanyTypeId={CompanyTypeId.Payee}>
              <LinkLoadPayAccount />
            </AuthRouteWithCompanyType>
          ),
          path: routes.linkLoadPayAccount,
        },
        {
          element: (
            <AuthRoute>
              <GeneralOAuth />
            </AuthRoute>
          ),
          path: routes.oAuth,
        },
        {
          element: (
            <AuthRoute>
              <SetPassword />
            </AuthRoute>
          ),
          path: routes.setPassword,
        },
        {
          element: (
            <AuthRoute>
              <TriumphInternalLandingPage />
            </AuthRoute>
          ),
          path: routes.triumph,
        },
        {
          element: (
            <AuthRoute>
              <BrokerOAuthLandingPage />
            </AuthRoute>
          ),
          path: `${routes.refreshToken}/:integrationId/landing`,
        },
        {
          element: (
            <AuthRoute>
              <BrokerOAuth />
            </AuthRoute>
          ),
          path: `${routes.refreshToken}/:integrationId`,
        },
        {
          // Only used for McLeod as of 10/21/19
          element: (
            <AuthRoute>
              <BrokerOAuth />
            </AuthRoute>
          ),
          path: `${routes.account}/:integrationName`,
        },
        {
          element: (
            <AuthRouteWithCompanyType permittedCompanyTypeId={CompanyTypeId.Factor}>
              <FourOhFour withNavBar />
            </AuthRouteWithCompanyType>
          ),
          path: 'factor/*',
        },
        {
          element: (
            <AuthRouteWithCompanyType permittedCompanyTypeId={CompanyTypeId.Payee}>
              <FourOhFour withNavBar />
            </AuthRouteWithCompanyType>
          ),
          path: 'payee/*',
        },
        {
          element: (
            <AuthRouteWithCompanyType permittedCompanyTypeId={CompanyTypeId.Payor}>
              <FourOhFour withNavBar />
            </AuthRouteWithCompanyType>
          ),
          path: 'payor/*',
        },
        {
          element: (
            <AuthRoute adminOnly>
              <FourOhFour withNavBar />
            </AuthRoute>
          ),
          path: 'admin/*',
        },
        ...(isProduction
          ? []
          : [
            {
              element: <Sandbox />,
              path: '/sandbox',
            },
            {
              element: <ColorDocs />,
              path: '/sandbox/color_docs',
            },
            {
              element: <Icons />,
              path: '/sandbox/icons',
            },
          ]),
        {
          element: (
            <AuthRoute>
              <FourOhFour withNavBar />
            </AuthRoute>
          ),
          path: '*',
        },
      ],
      element: (
        <Suspense fallback={<LoadingScreen />}>
          <Outlet />
        </Suspense>
      ),
      path: '/',
    },
  ],
  [isProduction]);
};
